import { getFeatureFlags, getUserDetail } from '@findigs/api/index.server';
import { invariantRedirect } from '@findigs/helpers/src/helpers/invariant-redirect.server';
import { serializeForScript } from '@findigs/helpers/src/helpers/serialize-for-script';
import { FeatureFlagsContext } from '@findigs/helpers/src/integrations/posthog/use-feature-flags';
import { usePostHogInitClient } from '@findigs/helpers/src/integrations/posthog/use-posthog-init-client';
import { usePylonPreloadScript } from '@findigs/helpers/src/integrations/pylon/pylon';
import { PylonContextProvider } from '@findigs/helpers/src/integrations/pylon/pylon-context';
import globalStyles from '@findigs/ui-kit/src/css/global.scss?url';
import faviconDark from '@findigs/ui-kit/src/images/favicon-dark.ico';
import favicon from '@findigs/ui-kit/src/images/favicon.ico';
import faviconPng from '@findigs/ui-kit/src/images/favicon.png';
import { json } from '@remix-run/node';
import type { LinksFunction, SerializeFrom } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
} from '@remix-run/react';
import type { FooterProps } from '~/components/footer';
import { Footer } from '~/components/footer';
import { GlobalLoadingIndicator } from '~/components/global-loading-indicator/global-loading-indicator';
import { Navbar } from '~/components/navbar';
import type { NavbarPureProps } from '~/components/navbar-pure';
import { loader as navbarLoader } from '~/components/navbar/navbar.server';
import appStyles from '~/css/app.scss?url';
import { RootErrorBoundary } from '~/error-boundary.root';
import { unsupportedBrowserRedirection } from '~/helpers/logic/unsupported-browser-redirection.server';
import { useSetJWTLocalStorage } from '~/hooks/use-set-jwt-local-storage';
import { useRudderStackIntegrationRoot } from '~/integrations/rudderstack';
import { getJWTSession } from '~/sessions/session-new.token.shim';
import type { AdvancedUserModel, RemixLoader } from '~/types';
import faviconMaskSvg from '@findigs/ui-kit/src/images/favicon-mask.raw.svg';
import faviconSvg from '@findigs/ui-kit/src/images/favicon.raw.svg';

export type WindowEnv = SerializeFrom<typeof loader>['ENV'];

export const links: LinksFunction = () => [
  { rel: 'apple-touch-icon', href: faviconPng },
  { rel: 'mask-icon', href: faviconMaskSvg, color: '#212B14' },
  { rel: 'shortcut icon', href: favicon, sizes: 'any', type: 'image/x-icon' },
  {
    rel: 'shortcut icon',
    href: faviconDark,
    sizes: 'any',
    type: 'image/x-icon',
    media: '(prefers-color-scheme: dark)',
  },
  { rel: 'shortcut icon', href: faviconPng, sizes: 'any', type: 'image/png' },
  {
    rel: 'shortcut icon',
    href: faviconSvg,
    sizes: 'any',
    type: 'image/svg+xml',
  },
  { rel: 'stylesheet', href: globalStyles },
  { rel: 'stylesheet', href: appStyles },
  { rel: 'dns-prefetch', href: 'https://widget.usepylon.com' },
  { rel: 'preconnect', href: 'https://widget.usepylon.com' },
];

function useRouteMatchData() {
  const matches = useMatches();

  let navbar: boolean | NavbarPureProps<any>['variant'] | undefined = undefined;
  let navbarLogoHref: string | false | undefined = undefined;
  let footer: boolean | FooterProps['variant'] | undefined = undefined;
  let background: NonNullable<RouteHandle>['background'] = undefined;

  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];

    navbar ??= match.handle?.navbar;
    footer ??= match.handle?.footer;
    navbarLogoHref ??= match.handle?.navbarLogoHref;
    background ??= match.handle?.background;
  }

  return {
    navbar,
    footer,
    background,
    navbarLogoHref: (navbarLogoHref ?? '/') || undefined,
  };
}

export const loader = async function (props) {
  await unsupportedBrowserRedirection(props);

  const { request } = props;
  const { pathname } = new URL(request.url);
  const navbarData = navbarLoader(props);

  // adds a dummy handler early to prevent uncaught errors from crashing the server
  navbarData.catch(() => 1);

  const { jwt, userId } = await getJWTSession(request);

  let user: AdvancedUserModel | undefined = undefined;
  if (jwt) {
    const { result: userResult } = await getUserDetail({ jwt });
    user = userResult;
  }

  const { result: featureFlagsResult } = await getFeatureFlags({ jwt });
  const featureFlags = featureFlagsResult?.flags;
  const isMaintenanceMode = featureFlags?.maintenance_mode;

  invariantRedirect(
    !isMaintenanceMode || pathname === '/maintenance',
    '/maintenance'
  );

  return json({
    ENV: {
      BUILD_ENV: process.env.BUILD_ENV ?? 'development',
      SENTRY_DSN: process.env.SENTRY_DSN ?? '',
      SENTRY_RELEASE: process.env.SENTRY_RELEASE ?? '',
      SENTRY_REPLAY_SAMPLE_RATE: process.env.SENTRY_REPLAY_SAMPLE_RATE ?? '0.0',
      SENTRY_TRACES_SAMPLE_RATE: process.env.SENTRY_TRACES_SAMPLE_RATE ?? '0.0',
      STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY ?? '',
      POSTHOG_PUBLISHABLE_KEY: process.env.POSTHOG_PUBLISHABLE_KEY ?? '',
      GOOGLE_MAPS_API_KEY: process.env.GOOGLE_MAPS_API_KEY ?? '',
      PYLON_CHAT_APP_ID: process.env.PYLON_CHAT_APP_ID ?? '',
    },
    navbarData: await navbarData,
    featureFlags,
    jwt,
    userId,
    user,
    showGlobalLoadingIndicator: process.env.BUILD_ENV !== 'production',
  });
} satisfies RemixLoader;

export default function App() {
  const { navbar, footer, navbarLogoHref, background } = useRouteMatchData();
  const {
    ENV,
    navbarData,
    jwt,
    userId,
    user,
    featureFlags,
    showGlobalLoadingIndicator,
  } = useLoaderData<typeof loader>();

  useSetJWTLocalStorage(jwt);
  useRudderStackIntegrationRoot({ userId });

  usePostHogInitClient({
    key: ENV.POSTHOG_PUBLISHABLE_KEY,
    distinctId: userId,
  });

  const preloaded = usePylonPreloadScript(ENV.PYLON_CHAT_APP_ID);

  return (
    <FeatureFlagsContext.Provider value={featureFlags}>
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <Meta />
          <Links />
          <script
            dangerouslySetInnerHTML={{
              __html: `window.ENV=${serializeForScript(ENV)}`,
            }}
          />
        </head>
        <body className={background}>
          <div id="app">
            <PylonContextProvider preloaded={preloaded} user={user}>
              {navbar !== false ? (
                <Navbar
                  loaderData={navbarData}
                  logoHref={navbarLogoHref}
                  variant={navbar === true ? undefined : navbar}
                />
              ) : null}
              {showGlobalLoadingIndicator && <GlobalLoadingIndicator />}
              <Outlet />
              {footer !== false ? (
                <Footer variant={footer === true ? undefined : footer} />
              ) : null}
              <ScrollRestoration />
              <Scripts />
            </PylonContextProvider>
          </div>
        </body>
      </html>
    </FeatureFlagsContext.Provider>
  );
}

export { RootErrorBoundary as ErrorBoundary };
